/**
 * OpenCASCADE.js Kernel Loader
 * Initializes the OpenCASCADE WASM geometry kernel for browser-side CAD operations
 *
 * This module handles lazy loading of the OpenCASCADE.js WASM module
 * Only loads when entering CAD mode to avoid unnecessary bundle size
 */

class GeometryKernelLoader {
  constructor() {
    this.kernel = null;          // OpenCASCADE.js instance
    this.isLoading = false;
    this.isLoaded = false;
    this.loadPromise = null;
    this.loadCallbacks = [];

    // Configuration
    this.config = {
      wasmUrl: '/static/js/libs/opencascade.wasm.js',  // Path to OpenCASCADE.js
      timeout: 120000  // 120 second timeout (97MB can take time on slow connections)
    };
  }

  /**
   * Initialize the geometry kernel (lazy load WASM)
   * Only loads once, subsequent calls return existing instance
   */
  async initialize() {
    // Already loaded
    if (this.isLoaded && this.kernel) {
      return this.kernel;
    }

    // Currently loading
    if (this.isLoading && this.loadPromise) {
      return this.loadPromise;
    }

    // Start loading
    this.isLoading = true;
    console.log('[GeometryKernel] Initializing OpenCASCADE.js WASM...');

    this.loadPromise = new Promise((resolve, reject) => {
      const startTime = performance.now();

      // Timeout handler
      const timeout = setTimeout(() => {
        reject(new Error('OpenCASCADE.js loading timed out'));
      }, this.config.timeout);

      // Wait for opencascade global to become available (async script loading)
      const checkForOpenCASCADE = () => {
        if (typeof window.opencascade !== 'undefined') {
          // OpenCASCADE loaded successfully
          clearTimeout(timeout);
          console.log('[GeometryKernel] OpenCASCADE.js found, initializing...');
          this._initializeKernel(window.opencascade)
            .then(() => {
              const loadTime = (performance.now() - startTime).toFixed(2);
              console.log(`[GeometryKernel] ✅ Loaded in ${loadTime}ms`);
              resolve(this.kernel);
            })
            .catch(reject);
        } else {
          // Not loaded yet, check again in 100ms
          setTimeout(checkForOpenCASCADE, 100);
        }
      };

      // Start checking
      checkForOpenCASCADE();
    });

    try {
      await this.loadPromise;
      this.isLoaded = true;
      this.isLoading = false;
      return this.kernel;
    } catch (error) {
      this.isLoading = false;
      this.loadPromise = null;
      throw error;
    }
  }

  /**
   * Initialize the OpenCASCADE module
   */
  async _initializeKernel(opencascadeModule) {
    if (typeof opencascadeModule === 'function') {
      // It's a factory function, call it
      this.kernel = await opencascadeModule();
    } else if (opencascadeModule.then) {
      // It's a promise
      this.kernel = await opencascadeModule;
    } else {
      // It's the module directly
      this.kernel = opencascadeModule;
    }

    // Verify kernel is ready
    if (!this.kernel || !this.kernel.FS) {
      throw new Error('OpenCASCADE kernel initialization failed');
    }

    console.log('[GeometryKernel] Kernel initialized');
  }

  /**
   * Get the loaded kernel instance
   * @throws Error if kernel not loaded
   */
  getKernel() {
    if (!this.isLoaded || !this.kernel) {
      throw new Error('Geometry kernel not initialized. Call initialize() first.');
    }
    return this.kernel;
  }

  /**
   * Check if kernel is ready
   */
  isReady() {
    return this.isLoaded && this.kernel !== null;
  }

  /**
   * Get kernel status
   */
  getStatus() {
    return {
      isLoading: this.isLoading,
      isLoaded: this.isLoaded,
      isReady: this.isReady()
    };
  }
}

// Global singleton instance
let kernelLoaderInstance = null;

/**
 * Get global kernel loader singleton
 */
function getGeometryKernelLoader() {
  if (!kernelLoaderInstance) {
    kernelLoaderInstance = new GeometryKernelLoader();
  }
  return kernelLoaderInstance;
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { GeometryKernelLoader, getGeometryKernelLoader };
}
